/**
 * design-tools/useGSAPReveal.js
 * Scroll-triggered reveal animations using IntersectionObserver
 * Mimics GSAP ScrollTrigger behavior without the bundle cost
 */
import { useEffect } from 'react';

export function useGSAPReveal(selector = '.gsap-reveal', options = {}) {
  useEffect(() => {
    const {
      threshold = 0.12,
      rootMargin = '0px 0px -60px 0px',
      staggerDelay = 80,
    } = options;

    const els = document.querySelectorAll(selector);
    if (!els.length) return;

    let counter = 0;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            const delay = parseFloat(el.dataset.revealDelay || 0) + counter * staggerDelay;
            setTimeout(() => {
              el.classList.add('gsap-revealed');
            }, delay);
            counter++;
            io.unobserve(el);
          }
        });
      },
      { threshold, rootMargin },
    );

    els.forEach((el) => io.observe(el));

    return () => io.disconnect();
  }, [selector]);
}

/**
 * useStaggerReveal
 * Reveals children of a container with staggered delays
 */
export function useStaggerReveal(containerSelector = '.stagger-container') {
  useEffect(() => {
    const containers = document.querySelectorAll(containerSelector);
    containers.forEach((container) => {
      const children = container.querySelectorAll('.stagger-child');
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              children.forEach((child, i) => {
                setTimeout(() => child.classList.add('stagger-visible'), i * 100);
              });
              io.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.1 },
      );
      io.observe(container);
    });
  }, [containerSelector]);
}
